import moment from "moment";
import { formatPrice } from "../../utils/formatPrice";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import type {
  ColDef,
  ValueGetterParams,
  ValueFormatterParams
} from "ag-grid-community";
import {
  MaritalStatusMapping,
  MortgageStatusMapping,
  type MaritalStatusEnum,
  type MortgageStatusEnum
} from "../../interfaces/mortgageDetailsInterface";

export const buyerTableColumnDefs: ColDef[] = [
  {
    field: "firstName",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "First Name",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "lastName",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Last Name",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "emailAddress",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Email",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "phoneNumber",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Phone Number",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatPhoneNumber(params.value) : "N/A"
  },
  {
    field: "status",
    cellDataType: "text",
    headerName: "Status",
    filter: true,
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value
        ? MortgageStatusMapping[params.value as MortgageStatusEnum] ||
          params.value
        : "N/A"
  },
  {
    field: "maritalStatus",
    cellDataType: "text",
    headerName: "Marital Status",
    filter: true,
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value
        ? MaritalStatusMapping[params.value as MaritalStatusEnum] ||
          params.value
        : "N/A"
  },
  {
    field: "isFirstTimeHomeBuyer",
    cellDataType: "text",
    headerName: "First Time Home Buyer",
    filter: "agTextColumnFilter",
    floatingFilter: true,
    valueGetter: (params: ValueGetterParams) => {
      if (params.data?.isFirstTimeHomeBuyer === true) {
        return "Yes";
      }
      if (params.data?.isFirstTimeHomeBuyer === false) {
        return "No";
      }
      return "N/A";
    },
    valueFormatter: (params: ValueFormatterParams) => params.value || "N/A"
  },
  {
    field: "pushNotification",
    cellDataType: "text",
    headerName: "Push Notification",
    filter: "agTextColumnFilter",
    floatingFilter: true,
    valueGetter: (params: ValueGetterParams) => {
      if (params.data?.pushNotification === true) {
        return "Yes";
      }
      if (params.data?.pushNotification === false) {
        return "No";
      }
      return "N/A";
    },
    valueFormatter: (params: ValueFormatterParams) => params.value || "N/A"
  },
  {
    field: "matchedListings",
    cellDataType: "number",
    headerName: "Matched Listings",
    filter: "agTextColumnFilter",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? params.value : "N/A";
    }
  },
  {
    field: "likedHomes",
    cellDataType: "number",
    headerName: "Liked Homes",
    filter: "agTextColumnFilter",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? params.value : "N/A";
    }
  },
  {
    field: "referralCode",
    cellDataType: "text",
    headerName: "Referral Code",
    filter: "agTextColumnFilter",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "maximumAffordability",
    cellDataType: "number",
    filter: "agNumberColumnFilter",
    headerName: "Maximum Affordability",
    floatingFilter: true,
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.maximumAffordability?.amount
        ? params.data.maximumAffordability.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.maximumAffordability?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalAnnualIncomeBuyerOnly",
    cellDataType: "number",
    headerName: "Total Annual Income Buyer Only",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalAnnualIncomeBuyerOnly?.amount
        ? params.data.totalAnnualIncomeBuyerOnly.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalAnnualIncomeBuyerOnly?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalDownPaymentBuyerOnly",
    cellDataType: "number",
    headerName: "Total Down Payment Buyer Only",
    floatingFilter: true,
    filter: "agNumberColumnFilter",

    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalDownPaymentBuyerOnly?.amount
        ? params.data.totalDownPaymentBuyerOnly.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalDownPaymentBuyerOnly?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalDebtBuyerOnly",
    cellDataType: "number",
    headerName: "Total Debt Buyer Only",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalDebtBuyerOnly?.amount
        ? params.data.totalDebtBuyerOnly.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalDebtBuyerOnly?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },

  {
    field: "totalMonthlyDebtBuyerOnly",
    cellDataType: "number",
    headerName: "Total Monthly Debt Buyer Only",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalMonthlyDebtBuyerOnly?.amount
        ? params.data.totalMonthlyDebtBuyerOnly.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalMonthlyDebtBuyerOnly?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "zownContribution",
    cellDataType: "number",
    headerName: "Zown Contribution",
    floatingFilter: true,
    filter: "agNumberColumnFilter",

    valueGetter: (params: ValueGetterParams) => {
      return params.data?.zownContribution?.amount
        ? params.data.zownContribution.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.zownContribution?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalDownPaymentNeeded",
    cellDataType: "number",
    headerName: "Total Down Payment Needed",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalDownPaymentNeeded?.amount
        ? params.data.totalDownPaymentNeeded.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalDownPaymentNeeded?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalNewDownPaymentNeeded",
    cellDataType: "number",
    headerName: "Total New Down Payment Needed",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalNewDownPaymentNeeded?.amount
        ? params.data.totalNewDownPaymentNeeded.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalNewDownPaymentNeeded?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "highestEligibleCreditScore",
    cellDataType: "number",
    headerName: "Highest Eligible Credit Score",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.highestEligibleCreditScore || null;
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? formatPrice(params.value) : "N/A";
    }
  },
  {
    field: "creditScore",
    cellDataType: "number",
    headerName: "Credit Score",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.creditScore || null;
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? formatPrice(params.value) : "N/A";
    }
  },

  {
    field: "totalAnnualIncomeWithCoApplicants",
    cellDataType: "number",
    headerName: "Total Annual Income With Co-Applicants",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalAnnualIncomeWithCoApplicants?.amount
        ? params.data.totalAnnualIncomeWithCoApplicants.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalAnnualIncomeWithCoApplicants?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalDebtWithCoApplicants",
    cellDataType: "number",
    headerName: "Total Debt With Co-Applicants",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalDebtWithCoApplicants?.amount
        ? params.data.totalDebtWithCoApplicants.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalDebtWithCoApplicants?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "totalMonthlyDebtWithCoApplicants",
    cellDataType: "number",
    headerName: "Total Monthly Debt With Co-Applicants",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.totalMonthlyDebtWithCoApplicants?.amount
        ? params.data.totalMonthlyDebtWithCoApplicants.amount / 100
        : null;
    },

    valueFormatter: (params: ValueFormatterParams) => {
      const amount = params.value;
      const currency = params.data?.totalMonthlyDebtWithCoApplicants?.currency;
      return amount ? `$${formatPrice(amount)} ${currency || ""}` : "N/A";
    },
    filterParams: {
      comparator: (filterValue: number, cellValue: number) => {
        if (!cellValue) return -1;
        if (cellValue < filterValue) return -1;
        if (cellValue > filterValue) return 1;
        return 0;
      },
      allowedCharPattern: "\\d\\-\\.",
      numberParser: (text: string) => Number.parseFloat(text)
    }
  },
  {
    field: "dateOfBirth",
    filter: "agDateColumnFilter",
    headerName: "Date of Birth",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "createdAt",
    filter: "agDateColumnFilter",
    headerName: "Created At",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "updatedAt",
    sort: "desc",
    filter: "agDateColumnFilter",
    headerName: "Updated At",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value
        ? `${moment(params.value).format("YYYY-MM-DD hh:mm a")}`
        : "N/A";
    },
    filterParams: {
      comparator: (filterDate: string, cellValue: string) => {
        if (!cellValue) return -1;

        const cellDate = moment(cellValue).startOf("day");
        const filterMoment = moment(filterDate).startOf("day");

        if (cellDate.isBefore(filterMoment)) return -1;
        if (cellDate.isAfter(filterMoment)) return 1;
        return 0;
      },
      browserDatePicker: true
    }
  },
  {
    field: "city",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "City",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "country",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Country",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "postalCode",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Postal Code",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  },
  {
    field: "province",
    cellDataType: "text",
    filter: "agTextColumnFilter",
    headerName: "Province",
    floatingFilter: true,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? params.value : "N/A"
  }
];
